// =============================================================================
// ResourceCombobox - Selettore risorsa con ricerca, autocompletamento e tastiera
// =============================================================================
// Digita per filtrare, scegli dal dropdown col mouse o con le frecce, conferma
// con Invio (seleziona il risultato evidenziato, di default il primo).
// Generico: accetta qualsiasi shape che esponga id/name (+ campi opzionali) e
// riceve le etichette via prop, così è riusabile in più contesti/namespace.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ResourceOption {
  id: string;
  name: string;
  color?: string | null;
  code?: string | null;
  resourceType?: { name?: string } | null;
}

interface ResourceComboboxProps {
  resources: ResourceOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Se fornito, mostra un'opzione "qualsiasi/nessuna" (value '') e consente di deselezionare. */
  emptyLabel?: string;
  /** Testo mostrato quando nessuna risorsa corrisponde alla ricerca. */
  noMatchLabel?: string;
  /** Variante compatta per pannelli densi. */
  size?: 'sm' | 'md';
  className?: string;
}

type Option = { id: string; resource?: ResourceOption };

export function ResourceCombobox({
  resources,
  value,
  onChange,
  placeholder,
  emptyLabel,
  noMatchLabel = '—',
  size = 'md',
  className,
}: ResourceComboboxProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedResource = useMemo(
    () => resources.find((r) => r.id === value),
    [resources, value],
  );

  const [query, setQuery] = useState(selectedResource?.name ?? '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Mantieni l'input allineato alla selezione quando cambia dall'esterno.
  useEffect(() => {
    setQuery(selectedResource?.name ?? '');
  }, [selectedResource]);

  // Quando l'input mostra esattamente il nome selezionato non stiamo filtrando:
  // mostra l'elenco completo (utile per ri-sfogliare dopo una selezione).
  const isFiltering = query.trim() !== '' && query !== selectedResource?.name;
  const filtered = useMemo(() => {
    if (!isFiltering) return resources;
    const q = query.trim().toLowerCase();
    return resources.filter((r) => {
      const haystack = `${r.name} ${r.resourceType?.name ?? ''} ${r.code ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [resources, query, isFiltering]);

  // Opzione "qualsiasi" solo quando non si sta filtrando (così Invio dopo una
  // ricerca seleziona il primo risultato reale, non l'opzione vuota).
  const options: Option[] = useMemo(() => {
    const opts: Option[] = filtered.map((r) => ({ id: r.id, resource: r }));
    if (emptyLabel && !isFiltering) opts.unshift({ id: '' });
    return opts;
  }, [filtered, emptyLabel, isFiltering]);

  // Reset evidenziazione all'apertura o al cambio dei risultati.
  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  // Chiudi cliccando fuori.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeAndSync();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedResource]);

  const closeAndSync = () => {
    setOpen(false);
    setQuery(selectedResource?.name ?? '');
  };

  const selectOption = (opt: Option) => {
    onChange(opt.id);
    setQuery(opt.resource?.name ?? '');
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      // Conferma l'opzione evidenziata (di default la prima) senza inviare il form.
      if (open && options.length > 0) {
        e.preventDefault();
        selectOption(options[Math.min(highlight, options.length - 1)]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeAndSync();
    }
  };

  const compact = size === 'sm';

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Search
          className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none',
            compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
          )}
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full input',
            compact ? 'pl-8 pr-7 py-1.5 text-xs' : 'pl-10 pr-9 py-2',
          )}
        />
        <ChevronDown
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform',
            compact ? 'w-3.5 h-3.5' : 'w-4 h-4',
            open && 'rotate-180',
          )}
        />
      </div>

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {options.length === 0 ? (
            <li className={cn('px-3 text-gray-400', compact ? 'py-1.5 text-xs' : 'py-2 text-sm')}>
              {noMatchLabel}
            </li>
          ) : (
            options.map((opt, idx) => {
              const r = opt.resource;
              return (
                <li
                  key={opt.id || '__empty__'}
                  role="option"
                  aria-selected={opt.id === value}
                  onMouseEnter={() => setHighlight(idx)}
                  // mousedown invece di click: previene il blur dell'input prima della selezione
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(opt);
                  }}
                  className={cn(
                    'flex items-center gap-2 px-3 cursor-pointer',
                    compact ? 'py-1.5 text-xs' : 'py-2 text-sm',
                    idx === highlight ? 'bg-emerald-50' : 'hover:bg-gray-50',
                  )}
                >
                  {r ? (
                    <>
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: r.color || '#6B7280' }}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="font-medium text-gray-900">{r.name}</span>
                        {r.resourceType?.name && (
                          <span className="text-gray-400 ml-1.5 text-xs">{r.resourceType.name}</span>
                        )}
                      </span>
                    </>
                  ) : (
                    <span className="flex-1 min-w-0 text-gray-500 italic">{emptyLabel}</span>
                  )}
                  {opt.id === value && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
